import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Badge,
  Pagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    // Mock user data - replace with Firebase queries
    setUsers([
      {
        id: '1',
        name: 'John Creator',
        email: 'john@example.com',
        avatar: '',
        role: 'creator',
        status: 'active',
        joinDate: new Date('2024-01-15'),
        lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000),
        totalEarnings: 1250.75,
        totalSpent: 500.00,
        campaignsCreated: 3,
        submissionsCount: 12,
        rating: 4.5,
        location: 'New York, USA',
        phone: '+1 555-0123'
      },
      {
        id: '2',
        name: 'Sarah Clipper',
        email: 'sarah@example.com',
        avatar: '',
        role: 'clipper',
        status: 'active',
        joinDate: new Date('2024-02-20'),
        lastActive: new Date(Date.now() - 30 * 60 * 1000),
        totalEarnings: 890.50,
        totalSpent: 0,
        campaignsCreated: 0,
        submissionsCount: 25,
        rating: 4.8,
        location: 'California, USA',
        phone: '+1 555-0456'
      },
      {
        id: '3',
        name: 'Mike Editor',
        email: 'mike@example.com',
        avatar: '',
        role: 'creator',
        status: 'suspended',
        joinDate: new Date('2023-12-10'),
        lastActive: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        totalEarnings: 0,
        totalSpent: 2100.00,
        campaignsCreated: 8,
        submissionsCount: 5,
        rating: 3.2,
        location: 'Texas, USA',
        phone: '+1 555-0789'
      }
    ]);
  }, []);

  const handleOpenDialog = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleUserAction = (action) => {
    // Handle user actions (suspend, activate, delete, etc.)
    console.log(`${action} user:`, selectedUser?.id);
    handleCloseDialog();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'creator':
        return 'primary';
      case 'clipper':
        return 'secondary';
      case 'admin':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const paginatedUsers = filteredUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog('create')}
          sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
        >
          Add User
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {users.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                {users.filter(u => u.status === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                {users.filter(u => u.role === 'creator').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Creators
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                {users.filter(u => u.role === 'clipper').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clippers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filterRole}
                label="Role"
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="creator">Creator</MenuItem>
                <MenuItem value="clipper">Clipper</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              fullWidth
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Join Date</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell>Earnings</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar src={user.avatar} sx={{ mr: 2 }}>
                      {user.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={user.role} 
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={user.status} 
                    color={getStatusColor(user.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.joinDate.toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {user.lastActive.toLocaleDateString()}
                </TableCell>
                <TableCell>
                  ${user.totalEarnings.toFixed(2)}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog('view', user)}>
                    <ViewIcon />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDialog('edit', user)}>
                    <EditIcon />
                  </IconButton>
                  {user.status === 'active' ? (
                    <IconButton onClick={() => handleOpenDialog('suspend', user)} color="error">
                      <BlockIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handleOpenDialog('activate', user)} color="success">
                      <ActivateIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredUsers.length / rowsPerPage)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* User Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedUser && (
          <>
            <DialogTitle>
              {dialogType === 'view' ? 'User Details' :
               dialogType === 'edit' ? 'Edit User' :
               dialogType === 'suspend' ? 'Suspend User' :
               dialogType === 'activate' ? 'Activate User' :
               'Create User'}
            </DialogTitle>
            <DialogContent>
              {dialogType === 'view' && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Avatar 
                        src={selectedUser.avatar} 
                        sx={{ width: 100, height: 100, margin: '0 auto 16px' }}
                      >
                        {selectedUser.name.charAt(0)}
                      </Avatar>
                      <Typography variant="h5">{selectedUser.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedUser.email}
                      </Typography>
                    </Box>
                    
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar><PhoneIcon /></Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary="Phone" 
                          secondary={selectedUser.phone || 'Not provided'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar><LocationIcon /></Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary="Location" 
                          secondary={selectedUser.location || 'Not specified'} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Statistics</Typography>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="Total Earnings" 
                          secondary={`$${selectedUser.totalEarnings.toFixed(2)}`} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Total Spent" 
                          secondary={`$${selectedUser.totalSpent.toFixed(2)}`} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Campaigns Created" 
                          secondary={selectedUser.campaignsCreated} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Submissions" 
                          secondary={selectedUser.submissionsCount} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Rating" 
                          secondary={`${selectedUser.rating}/5.0`} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              )}
              
              {(dialogType === 'suspend' || dialogType === 'activate') && (
                <Typography variant="body1">
                  Are you sure you want to {dialogType} user "{selectedUser.name}"?
                  {dialogType === 'suspend' && ' This will prevent them from accessing the platform.'}
                  {dialogType === 'activate' && ' This will restore their access to the platform.'}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              {dialogType === 'view' && (
                <Button 
                  onClick={() => handleOpenDialog('edit', selectedUser)}
                  variant="contained"
                >
                  Edit User
                </Button>
              )}
              {(dialogType === 'suspend' || dialogType === 'activate') && (
                <Button 
                  onClick={() => handleUserAction(dialogType)}
                  variant="contained"
                  color={dialogType === 'suspend' ? 'error' : 'success'}
                >
                  {dialogType === 'suspend' ? 'Suspend' : 'Activate'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
